import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart,
  ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis,
  AreaChart, Area, RadialBarChart, RadialBar, LineChart, Line,
} from 'recharts';
import {
  BarChart3, CheckCircle2, XCircle, Clock, CalendarClock, PlayCircle,
  ShieldAlert, ArrowRight, Search, ChevronRight, Calendar, LayoutGrid,
  ListTodo, X, Users, FileText, TrendingUp, Target, Sparkles,
  ArrowUpRight, ArrowDownRight, Minus, CalendarX,} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { useAuth } from '@/context/AuthContext';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import EmptyState from '@/components/ux/EmptyState';
import InterviewStatusBadge from '@/components/admin/InterviewStatusBadge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 6;

const SEGMENT_COLORS = {
  accepted: 'var(--eh-color-success)',
  rejected: 'var(--eh-color-error)',
  pending: 'var(--eh-color-warning)',
  terminated: 'hsl(var(--muted-foreground))',
};

function DashboardSkeleton() {
  return (
    <div className="mx-auto  space-y-4 pb-4">
      <div className="flex flex-wrap gap-3 rounded-md border border-border bg-card p-4">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-7 flex-1 min-w-[12rem]" />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-md" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
        <Skeleton className="h-72 rounded-md xl:col-span-5" />
        <Skeleton className="h-72 rounded-md xl:col-span-4" />
        <Skeleton className="h-72 rounded-md xl:col-span-3" />
      </div>
      <Skeleton className="h-64 rounded-md" />
    </div>
  );
}

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [recentFilter, setRecentFilter] = useState('all');
  const [pipeline, setPipeline] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const [statsRes, recentRes, pipelineRes] = await Promise.all([
          apiClient.getDashboardStats(),
          apiClient.getRecentInterviews(),
          apiClient.getPipelineSummary().catch(() => null),
        ]);
        setStats(statsRes.data);
        setRecent(recentRes.data);
        if (pipelineRes) setPipeline(pipelineRes.data);
      } catch {
        setStats(null);
        setRecent([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, recentFilter]);

  const greeting = user?.firstName ? `Welcome back, ${user.firstName}` : 'Welcome back';

  const pipelineCount = stats ? (stats.scheduled || 0) + (stats.inProgress || 0) : 0;

  const awaitingDecision =
    stats?.awaitingDecision ??
    Math.max(0, (stats?.pending ?? 0) - (stats?.scheduled || 0) - (stats?.inProgress || 0));

  const decided = stats ? (stats.accepted || 0) + (stats.rejected || 0) : 0;
  const acceptanceRate =
    decided > 0 ? Math.round(((stats.accepted || 0) / decided) * 100) : null;

  const monthly = stats?.monthlyCounts || [];
  const thisMonth = monthly[monthly.length - 1]?.count ?? 0;
  const priorMonth = monthly[monthly.length - 2]?.count ?? 0;
  const momChange =
    priorMonth > 0 ? Math.round(((thisMonth - priorMonth) / priorMonth) * 100) : thisMonth > 0 ? 100 : null;

  const avgRecentScore = useMemo(() => {
    const nums = recent.map((r) => parseFloat(String(r.score))).filter((n) => !Number.isNaN(n));
    if (!nums.length) return null;
    return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1);
  }, [recent]);

  const topTemplate = useMemo(() => {
    const counts = {};
    recent.forEach((r) => {
      const t = r.templateTitle?.trim() || '—';
      counts[t] = (counts[t] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0] || null;
  }, [recent]);

  const pieData = useMemo(() => {
    if (!stats) return [];
    const rows = [
      { name: 'Accepted', value: stats.accepted, key: 'accepted' },
      { name: 'Rejected', value: stats.rejected, key: 'rejected' },
      { name: 'Awaiting decision', value: awaitingDecision, key: 'pending' },
      { name: 'Terminated', value: stats.terminated, key: 'terminated' },
    ];
    return rows.filter((d) => d.value > 0);
  }, [stats, awaitingDecision]);

  const filteredRecent = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...recent].filter((row) => {
      if (!q) return true;
      return (
        row.candidateName?.toLowerCase().includes(q) ||
        row.templateTitle?.toLowerCase().includes(q) ||
        row.status?.toLowerCase().includes(q)
      );
    });
  }, [recent, search]);

  const statusFiltered = useMemo(() => {
    if (recentFilter === 'all') return filteredRecent;
    if (recentFilter === 'terminated') return filteredRecent.filter((r) => r.status === 'terminated');
    if (recentFilter === 'approved') return filteredRecent.filter((r) => r.decision?.status === 'approved');
    if (recentFilter === 'rejected') return filteredRecent.filter((r) => r.decision?.status === 'rejected');
    return filteredRecent;
  }, [filteredRecent, recentFilter]);

  const sortedRecent = useMemo(() => [...statusFiltered].reverse(), [statusFiltered]);

  const totalPages = Math.max(1, Math.ceil(sortedRecent.length / PAGE_SIZE));
  const paginatedRecent = sortedRecent.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (!stats) {
    return (
      <EmptyState
        icon={BarChart3}
        title="Could not load dashboard"
        description="We couldn't fetch analytics right now. Check your connection and try again."
        actionLabel="Retry"
        onAction={() => window.location.reload()}
      />
    );
  }

  const barData = stats.monthlyCounts || [];
  const filterButtons = [
    { id: 'all', label: 'All' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: 'terminated', label: 'Terminated' },
  ];

  return (
    <div className="mx-auto  space-y-4 pb-4">
      <div className="flex flex-col gap-3 rounded-md border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
            {/* <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Dashboard</span> */}
            {/* <span className="hidden text-muted-foreground sm:inline">·</span> */}
            <h1 className="text-base font-semibold tracking-tight text-foreground sm:text-lg">{greeting}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="font-mono tabular-nums">
              Month <span className="font-semibold text-foreground">{thisMonth}</span>
              {momChange != null && (
                <span className={momChange >= 0 ? ' text-success' : ' text-destructive'}>
                  {' '}
                  ({momChange >= 0 ? '+' : ''}
                  {momChange}%)
                </span>
              )}
            </span>
            <span className="hidden sm:inline">·</span>
            <span>
              Accept <span className="font-semibold text-foreground">{acceptanceRate != null ? `${acceptanceRate}%` : '—'}</span>
            </span>
            <span className="hidden sm:inline">·</span>
            <span>
              Avg score <span className="font-semibold text-foreground">{avgRecentScore != null ? `${avgRecentScore}%` : '—'}</span>
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3 sm:border-0 sm:pt-0">
          <Button size="sm" className="h-8 gap-1.5 px-3 text-xs" onClick={() => navigate('/admin/schedule')}>
            <Calendar className="h-3.5 w-3.5" aria-hidden />
            Schedule
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 px-3 text-xs" onClick={() => navigate('/admin/reports')}>
            <BarChart3 className="h-3.5 w-3.5" aria-hidden />
            Reports
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 px-3 text-xs" onClick={() => navigate('/admin/templates')}>
            <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
            Job Descriptions
          </Button>
        </div>
      </div>

      {/* ── Multi-round pipeline funnel ── */}
      {pipeline && (pipeline.funnel.total > 0) && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground/60">Candidate Pipeline</h2>
            <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-primary" onClick={() => navigate('/admin/candidate-process')}>
              View all <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { label: 'Total candidates', value: pipeline.funnel.total,       color: 'text-blue-500',    bg: 'bg-blue-500/10'    },
              { label: 'Active processes', value: pipeline.funnel.inProgress,  color: 'text-amber-500',   bg: 'bg-amber-500/10'   },
              { label: 'Hired',            value: pipeline.funnel.offerMade,   color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
              { label: 'Rejected',         value: pipeline.funnel.rejected,    color: 'text-red-500',     bg: 'bg-red-500/10'     },
              // Candidates who never sat the interview, and ones a termination
              // flagged. Both used to count as Rejected, which read as a hiring
              // decision nobody had made — three of five here were expiries.
              { label: "Didn't complete",  value: pipeline.funnel.lapsed ?? 0, color: 'text-orange-500',  bg: 'bg-orange-500/10'  },
              { label: 'Needs review',     value: pipeline.funnel.needsReview ?? 0, color: 'text-purple-500', bg: 'bg-purple-500/10' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`flex flex-col gap-1 rounded-md border border-border p-3 ${bg}`}>
                <span className="text-xs text-muted-foreground">{label}</span>
                <span className={`text-2xl font-bold tabular-nums ${color}`}>{value}</span>
              </div>
            ))}
          </div>
          {Object.keys(pipeline.stageCounts).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(pipeline.stageCounts).sort().map(([stage, count]) => (
                <span key={stage} className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {stage}: <span className="font-bold">{count}</span>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground/60">Overview</h2>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {/* Units are mixed here on purpose — some questions are about
              sessions, some about people — so each card says which. Accepted,
              rejected and awaiting used to count sessions, reading 54 hires
              against 4 people actually hired. */}
          <DashboardKpiCard title="Total interviews" value={stats.total} subtitle="All sessions in scope" icon={BarChart3} tone="primary" />
          <DashboardKpiCard title="Live pipeline" value={pipelineCount} subtitle={`${stats.scheduled || 0} scheduled · ${stats.inProgress || 0} live${stats.disconnected ? ` · ${stats.disconnected} disconnected` : ''}`} icon={PlayCircle} tone="warning" />
          <DashboardKpiCard title="Awaiting decision" value={awaitingDecision} subtitle="Candidates interviewed, no verdict" icon={Clock} tone="default" />
          <DashboardKpiCard title="Accepted (hires)" value={stats.accepted} subtitle={acceptanceRate != null ? `${acceptanceRate}% of decided candidates` : 'Candidates hired'} icon={CheckCircle2} tone="success" />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
          {/* expired and disconnected were counted by no card at all, yet are a
              fifth of all sessions — expired being the second largest status. */}
          <DashboardKpiCard
            title="Missed / expired"
            value={(stats.expired ?? 0) + (stats.noShow ?? 0)}
            subtitle={`${stats.expired ?? 0} expired · ${stats.noShow ?? 0} no show`}
            icon={CalendarX}
            tone={(stats.expired ?? 0) > 0 ? 'warning' : 'default'}
          />
          <DashboardKpiCard title="Candidates" value={stats.candidatesCount ?? '—'} subtitle="Registered profiles" icon={Users} tone="default" />
          <DashboardKpiCard title="Templates" value={stats.templatesCount ?? '—'} subtitle="Interview blueprints" icon={FileText} tone="primary" />
          <DashboardKpiCard title="Reports generated" value={stats.reportsCount ?? '—'} subtitle={stats.total > 0 ? `${Math.round(((stats.reportsCount||0)/stats.total)*100)}% of sessions` : 'No sessions yet'} icon={TrendingUp} tone="success" />
          <DashboardKpiCard title="Avg AI score" value={avgRecentScore != null ? `${avgRecentScore}%` : '—'} subtitle="Last 10 completed sessions" icon={Target} tone={avgRecentScore != null && parseFloat(avgRecentScore) >= 70 ? 'success' : 'warning'} />
        </div>
      </section>

      {/* ── Insights strip ── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          {
            label: 'This month',
            value: thisMonth,
            sub: momChange != null ? `${momChange >= 0 ? '+' : ''}${momChange}% vs last month` : 'First month',
            icon: BarChart3,
            positive: momChange != null && momChange >= 0,
            arrow: momChange,
          },
          {
            label: 'Acceptance rate',
            value: acceptanceRate != null ? `${acceptanceRate}%` : '—',
            sub: `${stats.accepted || 0} accepted · ${stats.rejected || 0} rejected`,
            icon: CheckCircle2,
            positive: acceptanceRate != null && acceptanceRate >= 50,
          },
          {
            label: 'Terminated / No-show',
            value: (stats.terminated || 0) + (stats.noShow || 0),
            sub: `${stats.terminated || 0} terminated · ${stats.noShow || 0} no-show`,
            icon: ShieldAlert,
            positive: (stats.terminated || 0) + (stats.noShow || 0) === 0,
          },
          {
            label: 'Top template',
            value: topTemplate ? topTemplate[0] : '—',
            sub: topTemplate ? `${topTemplate[1]} session${topTemplate[1] !== 1 ? 's' : ''}` : 'No data yet',
            icon: Sparkles,
            positive: true,
          },
        ].map(({ label, value, sub, icon: Icon, positive, arrow }) => (
          <div key={label} className={cn(
            'flex items-start gap-2.5 rounded-md border p-3',
            'border-border bg-card'
          )}>
            <div className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
              positive ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
            )}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/65 truncate">{label}</p>
              <p className="text-sm font-black text-foreground truncate">{value}</p>
              <p className="text-[10px] text-foreground/60 truncate">{sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12 xl:items-stretch">
        <Card className="flex h-full min-h-0 flex-col rounded-md border-border shadow-sm xl:col-span-5">
          <CardHeader className="space-y-0 border-b border-border/60 py-3">
            <CardTitle className="text-sm font-semibold">Details</CardTitle>
            <CardDescription className="text-xs">By category</CardDescription>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 px-3 pb-3 pt-2">
            <Tabs defaultValue="hiring" className="w-full">
            <TabsList className="mb-2 grid h-auto w-full grid-cols-3 gap-0.5 p-0.5 sm:w-fit sm:grid-cols-3">
              <TabsTrigger value="hiring" className="text-[11px] sm:text-xs">
                Decisions
              </TabsTrigger>
              <TabsTrigger value="ops" className="text-[11px] sm:text-xs">
                Operations
              </TabsTrigger>
              <TabsTrigger value="library" className="text-[11px] sm:text-xs">
                Directory
              </TabsTrigger>
            </TabsList>
            <TabsContent value="hiring" className="mt-0 space-y-2 outline-none">
              {[
                { label: 'Rejected', value: stats.rejected, hint: 'Post-interview decline', icon: XCircle },
                { label: 'Terminated', value: stats.terminated, hint: 'Proctoring / policy', icon: ShieldAlert },
                {
                  label: 'Pending (rollup)',
                  value: stats.pending,
                  hint: 'Scheduled + live + awaiting decision',
                  icon: CalendarClock,
                },
              ].map(({ label, value, hint, icon: Icon }) => (
                <div
                  key={label}
                  className="group flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2.5 transition-colors hover:bg-muted/35"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30 text-muted-foreground">
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground">{hint}</p>
                    </div>
                  </div>
                  <span className="font-mono text-xl font-bold tabular-nums text-foreground">{value}</span>
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 w-full gap-2 text-muted-foreground hover:text-primary"
                onClick={() => navigate('/admin/reports')}
              >
                Open reports workspace
                <ChevronRight className="h-4 w-4" />
              </Button>
            </TabsContent>
            <TabsContent value="ops" className="mt-0 grid gap-2 sm:grid-cols-2 outline-none">
              {[
                { label: 'Scheduled', value: stats.scheduled ?? 0 },
                { label: 'In progress', value: stats.inProgress ?? 0 },
                { label: 'Reports generated', value: stats.reportsCount ?? '—' },
                { label: 'Cancelled', value: stats.cancelled ?? 0 },
                { label: 'No-show', value: stats.noShow ?? 0 },
                { label: 'Completed (total)', value: stats.completed ?? '—' },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between rounded-md border border-border bg-muted/15 px-3 py-2.5 transition-colors hover:bg-muted/25"
                >
                  <span className="text-sm font-medium text-foreground">{row.label}</span>
                  <span className="font-mono text-lg font-bold tabular-nums">{row.value}</span>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="library" className="mt-0 grid gap-2 sm:grid-cols-2 outline-none">
              {[
               { label: 'Candidate profiles', value: stats.candidatesCount ?? '—', to: '/admin/create-user' },
               { label: 'Interview templates', value: stats.templatesCount ?? '—', to: '/admin/templates' },
              ].map((row) => (
                <button
                  key={row.label}
                  type="button"
                  onClick={() => navigate(row.to)}
                  className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/15 px-3 py-2.5 text-left transition-colors hover:bg-muted/25"
                >
                  <span className="text-sm font-medium text-foreground">{row.label}</span>
                  <span className="flex items-center gap-1 font-mono text-lg font-bold tabular-nums">
                    {row.value}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                  </span>
                </button>
              ))}
            </TabsContent>
          </Tabs>
          </CardContent>
        </Card>

        <section className="flex min-h-0 min-w-0 flex-col rounded-md border border-border bg-card shadow-sm xl:col-span-4">
          <Tabs defaultValue="distribution" className="flex w-full min-h-0 flex-1 flex-col px-3 pb-3 pt-3">
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-foreground">Analytics</h2>
              <TabsList className="grid h-8 w-full max-w-sm grid-cols-3 rounded-md bg-muted/50 p-0.5 sm:w-auto">
                <TabsTrigger value="distribution" className="text-[11px] sm:text-xs">Outcomes</TabsTrigger>
                <TabsTrigger value="volume" className="text-[11px] sm:text-xs">Volume</TabsTrigger>
                <TabsTrigger value="trend" className="text-[11px] sm:text-xs">Trend</TabsTrigger>
              </TabsList>
            </div>
          <TabsContent
            value="distribution"
            className="mt-0 rounded-md border border-border bg-muted/10"
          >
            <Card className="border-0 bg-transparent shadow-none">
              <CardHeader className="space-y-0 p-3 pb-0">
                <CardTitle className="text-sm">Outcomes</CardTitle>
                <CardDescription className="text-xs">Hire vs decline vs open vs terminated</CardDescription>
              </CardHeader>
              <CardContent className="p-3 pt-2">
                {pieData.length === 0 ? (
                  <p className="flex h-52 items-center justify-center px-2 text-center text-xs leading-relaxed text-muted-foreground">
                    Not enough outcome data yet. When completed interviews have hire decisions, segments appear here.
                  </p>
                ) : (
                  <>
                    <div className="h-52 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={44}
                            outerRadius={72}
                            paddingAngle={2}
                            labelLine={false}
                            isAnimationActive
                          >
                            {pieData.map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={SEGMENT_COLORS[entry.key]}
                                stroke="hsl(var(--background))"
                                strokeWidth={2}
                              />
                            ))}
                          </Pie>
                          <ReTooltip
                            contentStyle={{
                              background: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '12px',
                              color: 'hsl(var(--foreground))',
                            }}
                          itemStyle={{ color: 'hsl(var(--foreground))' }}
                          labelStyle={{ color: 'hsl(var(--foreground) / 0.6)' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1.5">
                      {pieData.map((entry) => (
                        <div key={entry.name} className="flex items-center gap-1.5 text-xs">
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: SEGMENT_COLORS[entry.key] }}
                            aria-hidden
                          />
                          <span className="text-foreground/70">
                            {entry.name}: <span className="font-semibold text-foreground">{entry.value}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent
            value="volume"
            className="mt-0 rounded-md border border-border bg-muted/10"
          >
            <Card className="border-0 bg-transparent shadow-none">
              <CardHeader className="space-y-0 p-3 pb-0">
                <CardTitle className="text-sm">Volume</CardTitle>
                <CardDescription className="text-xs">Sessions started by month</CardDescription>
              </CardHeader>
              <CardContent className="p-3 pt-2">
                <div className="h-52 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} margin={{ top: 8, right: 6, left: -14, bottom: 2 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <ReTooltip
                        cursor={{ fill: 'hsl(var(--muted) / 0.35)', radius: 6 }}
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px',
                          color: 'hsl(var(--foreground))',
                        }}
                        itemStyle={{ color: 'hsl(var(--foreground))' }}
                        labelStyle={{ color: 'hsl(var(--foreground) / 0.6)' }}
                      />
                      <Bar
                        dataKey="count"
                        fill="hsl(var(--primary))"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={36}
                        isAnimationActive
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="trend" className="mt-0 rounded-md border border-border bg-muted/10">
            <Card className="border-0 bg-transparent shadow-none">
              <CardHeader className="space-y-0 p-3 pb-0">
                <CardTitle className="text-sm">Session Trend</CardTitle>
                <CardDescription className="text-xs">Interviews started per month (area view)</CardDescription>
              </CardHeader>
              <CardContent className="p-3 pt-2">
                <div className="h-52 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={barData} margin={{ top: 8, right: 6, left: -14, bottom: 2 }}>
                      <defs>
                        <linearGradient id="adminAreaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="month" tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <ReTooltip cursor={{ fill: 'hsl(var(--muted) / 0.35)', radius: 6 }}
                        contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px', color: 'hsl(var(--foreground))' }}
                        itemStyle={{ color: 'hsl(var(--foreground))' }}
                        labelStyle={{ color: 'hsl(var(--foreground) / 0.6)' }}
                        formatter={v => [v, 'Sessions']} />
                      <Area type="monotone" dataKey="count" name="Sessions" stroke="hsl(var(--primary))"
                        strokeWidth={2.5} fill="url(#adminAreaGrad)"
                        dot={{ fill: 'hsl(var(--primary))', r: 3, strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 0 }} isAnimationActive />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          </Tabs>
        </section>

        <Card className="flex min-h-0 flex-col rounded-md border-border shadow-sm xl:col-span-3">
          <CardHeader className="space-y-0 border-b border-border/60 py-3">
            <div className="flex items-center gap-2">
              <ListTodo className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <CardTitle className="text-sm font-semibold">Queue & activity</CardTitle>
            </div>
            <CardDescription className="text-xs">Next steps and latest completions</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col gap-3 px-3 pb-3 pt-2">
            <div className="space-y-1.5">
              {[
                {
                  key: 'decisions',
                  label: 'Pending hire decisions',
                  n: awaitingDecision,
                  to: '/admin/reports',
                  show: awaitingDecision > 0,
                },
                {
                  key: 'live',
                  label: 'In progress',
                  n: stats.inProgress ?? 0,
                  to: '/admin/reports',
                  show: (stats.inProgress ?? 0) > 0,
                },
                {
                  key: 'sched',
                  label: 'Scheduled',
                  n: stats.scheduled ?? 0,
                  to: '/admin/schedule',
                  show: (stats.scheduled ?? 0) > 0,
                },
              ]
                .filter((r) => r.show)
                .map((row) => (
                  <button
                    key={row.key}
                    type="button"
                    onClick={() => navigate(row.to)}
                    className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/50"
                  >
                    <span className="font-medium text-foreground">{row.label}</span>
                    <span className="font-mono text-sm font-semibold tabular-nums text-foreground">{row.n}</span>
                  </button>
                ))}
              {awaitingDecision === 0 &&
                (stats.inProgress ?? 0) === 0 &&
                (stats.scheduled ?? 0) === 0 && (
                  <p className="rounded-md border border-dashed border-border px-2.5 py-2 text-[11px] leading-relaxed text-muted-foreground">
                    No items in these buckets. Scheduled or in-progress work will show here.
                  </p>
                )}
            </div>
            {(stats.terminated > 0 || stats.noShow > 0) && (
              <p className="text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">{stats.terminated}</span> terminated
                <span className="mx-1.5 text-border">·</span>
                <span className="font-medium text-foreground">{stats.noShow}</span> no-show
              </p>
            )}
            <Separator />
            <div className="min-h-0 flex-1">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Latest completed
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px] text-primary"
                  onClick={() => navigate('/admin/reports')}
                >
                  All reports
                  <ChevronRight className="ml-0.5 h-3 w-3" />
                </Button>
              </div>
              {recent.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">No completed interviews in the recent feed yet.</p>
              ) : (
                <ul className="space-y-0.5">
                  {recent.slice(0, 6).map((r) => (
                    <li key={String(r.sessionId)}>
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/reports/${r.sessionId}`)}
                        className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs transition-colors hover:bg-muted/60"
                      >
                        <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                          {r.candidateName || '—'}
                        </span>
                        <InterviewStatusBadge row={r} className="h-5 shrink-0 px-1.5 text-[10px] font-normal" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {topTemplate && (
              <p className="text-[11px] text-muted-foreground">
                Frequent template in sample:{' '}
                <span className="font-medium text-foreground">{topTemplate[0]}</span>
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-hidden rounded-md border-border shadow-sm">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-card px-3 py-2">
          <div className="flex shrink-0 items-center gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent interviews</h3>
            <Badge variant="secondary" className="h-5 px-1.5 font-mono text-[10px] tabular-nums font-normal">
              {sortedRecent.length}
            </Badge>
          </div>
          <div className="relative min-w-[10rem] flex-1 basis-[min(100%,14rem)] sm:max-w-[240px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Candidate, template, status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 border-border/70 bg-background py-1 pl-8 pr-8 text-xs"
              aria-label="Search recent interviews"
            />
            {search.trim() !== '' && (
              <button
                type="button"
                className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filter interviews">
            {filterButtons.map((f) => (
              <Button
                key={f.id}
                type="button"
                size="sm"
                variant={recentFilter === f.id ? 'default' : 'outline'}
                className={cn(
                  'h-7 shrink-0 rounded-md px-2 text-[11px]',
                  recentFilter !== f.id && 'border-border/70 bg-transparent shadow-none'
                )}
                onClick={() => setRecentFilter(f.id)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        <CardContent className="p-0">
          <Table className="text-xs [&_td]:py-2 [&_td]:px-2.5 [&_th]:py-2 [&_th]:px-2.5">
            <TableHeader>
              <TableRow className="border-border/60 bg-muted/40 hover:bg-muted/40">
                <TableHead className="w-[min(28%,11rem)] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Candidate
                </TableHead>
                <TableHead className="hidden max-w-[9rem] truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell">
                  Template
                </TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Status
                </TableHead>
                <TableHead className="hidden w-[4.5rem] text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:table-cell">
                  Score
                </TableHead>
                <TableHead className="hidden w-[5.5rem] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground lg:table-cell">
                  Date
                </TableHead>
                <TableHead className="w-10 pr-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span className="sr-only">Open</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRecent.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6} className="h-24 text-center text-[11px] text-muted-foreground">
                    No rows match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecent.map((row, idx) => (
                  <TableRow
                    key={row.sessionId}
                    style={{ animationDelay: `${idx * 24}ms` }}
                    className={cn(
                      'group cursor-pointer border-border/40 transition-colors animate-in fade-in',
                      'hover:bg-muted/50'
                    )}
                    onClick={() => navigate(`/admin/reports/${row.sessionId}`)}
                  >
                    <TableCell className="max-w-[10rem] truncate font-medium text-foreground">{row.candidateName}</TableCell>
                    <TableCell className="hidden max-w-[9rem] truncate text-muted-foreground sm:table-cell">
                      {row.templateTitle || '—'}
                    </TableCell>
                    <TableCell>
                      <InterviewStatusBadge row={row} className="h-5 px-1.5 text-[10px] font-normal" />
                    </TableCell>
                    <TableCell className="hidden text-right font-mono tabular-nums font-semibold text-primary md:table-cell">
                      {row.score ? `${parseFloat(row.score).toFixed(1)}%` : '—'}
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap text-muted-foreground lg:table-cell">
                      {row.date || '—'}
                    </TableCell>
                    <TableCell className="p-1 pr-2 text-right align-middle">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-primary">
                        <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden />
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {sortedRecent.length > PAGE_SIZE && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 bg-muted/15 px-3 py-2">
              <p className="text-[11px] tabular-nums text-muted-foreground">
                <span className="font-medium text-foreground">{page}</span> / {totalPages}
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPanel;
